import React, {forwardRef, useRef, useState} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    Box,
    Skeleton as ChakraSkeleton,
    Text,
    useToast,
    Button,
    useDisclosure
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useShopperCustomersMutation, useStores} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {pxToRem} from '../../../../utills/pxToRem'
import {ToggleCard} from '../../../../components/toggle-card'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import StoreSelectorModal from '../../../../components/store-selector-modal'
import DisplayMyStoreDetails from './display-my-store-details'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'

// eslint-disable-next-line react/prop-types
const Skeleton = forwardRef(({children, height, width, ...rest}, ref) => {
    const {data: customer} = useCurrentCustomer()
    const {isRegistered} = customer
    const size = !isRegistered
        ? {
              height,
              width
          }
        : {}
    return (
        <ChakraSkeleton ref={ref} isLoaded={!customer.isLoading} {...rest} {...size}>
            {children}
        </ChakraSkeleton>
    )
})
Skeleton.displayName = 'Skeleton'

const MyStoreCard = () => {
    const {formatMessage} = useIntl()
    const headingRef = useRef(null)
    const {data: customer, refetch} = useCurrentCustomer()
    const {site} = useMultiSite()
    const toast = useToast()
    const {customerId} = customer
    const [isLoadingDetails, setIsLoadingDetails] = useState(false)
    const updateCustomerMutation = useShopperCustomersMutation('updateCustomer')
    const {
        isOpen: isOpenStoreSelector,
        onOpen: onOpenStoreSelector,
        onClose: onCloseStoreSelector
    } = useDisclosure()

    const openStoreModal = () => {
        onOpenStoreSelector()
    }

    // Get store details
    const {data: preferredStoreDetails} = useStores(
        {
            parameters: {
                siteId: site.id,
                ids: customer.c_preferredStore
            }
        },
        {
            enabled: !!customer.c_preferredStore
        }
    )

    const submit = (selectedStoreId) => {
        try {
            setIsLoadingDetails(true)
            updateCustomerMutation.mutate(
                {
                    parameters: {customerId},
                    body: {
                        c_preferredStore: selectedStoreId
                    }
                },
                {
                    onSuccess: async () => {
                        toast({
                            title: formatMessage({
                                defaultMessage: 'Store updated',
                                id: 'my_store.info.store_updated'
                            }),
                            status: 'success',
                            isClosable: true,
                            position: 'top-right'
                        })
                        headingRef?.current?.focus()
                        setIsLoadingDetails(false)
                        await refetch()
                    }
                }
            )
        } catch (error) {
            console.log(error)
        }
    }

    const handleRemoveStore = () => {
        submit('')
        setIsLoadingDetails(true)
    }

    const handleUpdateSore = (selectedStoreId) => {
        submit(selectedStoreId)
    }

    return (
        <ToggleCard
            id="myStore"
            title={
                <Skeleton ref={headingRef} tabIndex="-1">
                    <FormattedMessage defaultMessage="My store" id="my_store_card.title.my_store" />
                </Skeleton>
            }
            displayPreIcons={false}
            marginTop="0 important"
            titleStyles={{
                color: 'black',
                fontSize: {base: pxToRem(16), lg: pxToRem(18)},
                letterSpacing: {base: pxToRem(2), lg: pxToRem(3)},
                fontWeight: 500,
                lineHeight: 'normal',
                marginBottom: pxToRem(10)
            }}
            paddingLeft={{base: pxToRem(15), lg: pxToRem(18)}}
            paddingRight={{base: pxToRem(15), lg: pxToRem(18)}}
            marginLeft={{base: pxToRem(10), lg: 0}}
            marginRight={{base: pxToRem(10), lg: 0}}
        >
            {preferredStoreDetails &&
            Object.keys(preferredStoreDetails)?.length &&
            customer.c_preferredStore ? (
                <DisplayMyStoreDetails storeDetails={preferredStoreDetails.data[0]} />
            ) : (
                <Text
                    fontSize={{base: pxToRem(12), lg: pxToRem(13)}}
                    color="black"
                    lineHeight={'normal'}
                    fontWeight={400}
                    letterSpacing={pxToRem(1)}
                >
                    {formatMessage({
                        id: 'my_store.selectSore.label.no_prefered_store_selected',
                        defaultMessage: 'You do not currently have a preferred store selected.'
                    })}
                </Text>
            )}

            <Box
                position="absolute"
                right={pxToRem(18)}
                display={'flex'}
                gap={{base: pxToRem(10), lg: pxToRem(15)}}
                bottom={{base: pxToRem(15), lg: pxToRem(18)}}
            >
                {preferredStoreDetails && Object.keys(preferredStoreDetails)?.length && (
                    <Button
                        variant="red-link-uline"
                        onClick={handleRemoveStore}
                        fontWeight={500}
                        fontSize={{base: '10px', md: '12px'}}
                        color="cav_calltoaction_darkred"
                        lineHeight="normal"
                        letterSpacing={pxToRem(1)}
                        borderRight={'1px solid'}
                        borderColor={'cav_pdp_dark_gray'}
                        paddingRight={{base: pxToRem(10), lg: pxToRem(15)}}
                        borderRadius={0}
                    >
                        {formatMessage({
                            id: 'my_store.selectSore.label.remove',
                            defaultMessage: 'Remove'
                        })}
                    </Button>
                )}
                <Button
                    variant="red-link-uline"
                    onClick={openStoreModal}
                    fontWeight={500}
                    fontSize={{base: pxToRem(10), md: pxToRem(12)}}
                    color="cav_calltoaction_darkred"
                    lineHeight="normal"
                    letterSpacing={pxToRem(1)}
                >
                    {preferredStoreDetails && Object.keys(preferredStoreDetails)?.length
                        ? formatMessage({
                              id: 'my_store.selectSore.label.change_my_store',
                              defaultMessage: 'change My Store'
                          })
                        : formatMessage({
                              id: 'my_store.selectSore.label.select_my_store',
                              defaultMessage: 'Select my store'
                          })}
                </Button>
            </Box>
            {isLoadingDetails && <LoadingSpinner position="absolute" zIndex={1} />}
            {/* store selector modal */}
            <StoreSelectorModal
                handleUpdateSore={handleUpdateSore}
                isOpen={isOpenStoreSelector}
                onClose={onCloseStoreSelector}
            />
        </ToggleCard>
    )
}

export default MyStoreCard
